// Objednávky (310): stavy, termíny a skupiny na jednom místě — seznam, tabule,
// kalendář i Dodávky na telefonu mluví stejnou řečí. Data drží server
// (bucket, late, no_term, pickup_wait, paid, done); tady je jen jejich text.
const TZ = "Europe/Prague";
const DOW = ["ne", "po", "út", "st", "čt", "pá", "so"];

export const EDIT_ROLES = ["OWNER", "ASSISTANT", "MANAGER"];
export const RECEIVE_ROLES = ["OWNER", "ASSISTANT", "MANAGER", "WORKER", "SUBCONTRACTOR", "WAREHOUSE"];
export const OPEN = ["ordered", "confirmed", "shipped", "ready"];

export const localDay = (value = new Date()) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" })
    .format(typeof value === "string" ? new Date(value) : value);
export const todayISO = () => localDay(new Date());
const at = (iso) => new Date(`${iso}T12:00:00Z`);
export const addDays = (iso, n) => { const d = at(iso); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
export const daysBetween = (a, b) => Math.round((at(b) - at(a)) / 864e5);
export const dow = (iso) => DOW[at(iso).getUTCDay()];
export const dm = (iso) => `${Number(iso.slice(8, 10))}. ${Number(iso.slice(5, 7))}.`;
export const mondayOf = (iso) => { const g = at(iso).getUTCDay(); return addDays(iso, g === 0 ? -6 : 1 - g); };

const dny = (n) => `${n} ${n === 1 ? "den" : n >= 2 && n <= 4 ? "dny" : "dní"}`;

export const STATUS = {
  ordered: { label: "Objednáno", pill: "bg-secondary text-muted-foreground", dot: "bg-muted-foreground" },
  confirmed: { label: "Potvrzeno", pill: "bg-blue-500/10 text-blue-700 dark:text-blue-300", dot: "bg-blue-500" },
  shipped: { label: "Na cestě", pill: "bg-brand/10 pp-ink", dot: "bg-brand" },
  ready: { label: "K vyzvednutí", pill: "bg-amber-500/15 text-amber-800 dark:text-amber-300", dot: "bg-amber-500" },
  received: { label: "Převzato", pill: "bg-success/15 text-success", dot: "bg-success" },
  cancelled: { label: "Zrušeno", pill: "bg-secondary text-muted-foreground", dot: "bg-muted-foreground/60" },
};
const LATE = { label: "Nedorazilo", pill: "bg-destructive/10 text-destructive", dot: "bg-destructive" };
const DONE = { label: "Vyřízeno", pill: "bg-success/15 text-success", dot: "bg-success" };

export const pillOf = (o) => (o.done ? DONE : o.late ? LATE : STATUS[o.status] || STATUS.ordered);

// Termín řečí stavby: dnes / zítra / čt 22. 10. / od 19. 10. (k vyzvednutí) / bez termínu.
export function dueText(o, today = todayISO()) {
  if (o.status === "received") return o.received?.at ? `převzato ${dm(localDay(o.received.at))}` : "převzato";
  if (o.status === "cancelled") return "zrušeno";
  if (!o.due_date) return "bez termínu";
  const d = daysBetween(today, o.due_date);
  if (o.status === "ready") return d === 0 ? "od dnes" : `od ${dm(o.due_date)}`;
  if (d === 0) return "dnes";
  if (d === 1) return "zítra";
  if (d === -1) return "včera";
  if (d > 1 && d < 7) return `${dow(o.due_date)} ${dm(o.due_date)}`;
  return dm(o.due_date);
}

// Proč objednávka volá o pozornost (červeně = problém, žlutě = hlídat). null = v pořádku.
export function warnOf(o, today = todayISO()) {
  if (o.problem) return { tone: "red", text: o.problem.kind === "damaged" ? "poškozené" : "něco chybí" };
  if (o.late) return { tone: "red", text: `o ${dny(daysBetween(o.due_date, today))} pozdě` };
  if (o.no_term) return { tone: "amber", text: `doplň termín · ${dny(daysBetween(localDay(o.created_at), today))}` };
  if (o.pickup_wait) return { tone: "amber", text: `čeká ${dny(daysBetween(o.due_date, today))}` };
  if ((o.returns || []).some((r) => r.status === "to_return")) return { tone: "amber", text: "vratka" };
  if (o.status === "received" && !o.paid) return { tone: "amber", text: "čeká na platbu" };
  return null;
}

export const MODES = {
  site: { label: "Na stavbu", short: "na stavbu" },
  pickup: { label: "Vyzvednout na pobočce", short: "osobní odběr" },
  office: { label: "Do kanceláře", short: "do kanceláře" },
};

export function deliveryText(o) {
  const d = o.delivery || {};
  if (d.mode === "pickup") return d.branch || "osobní odběr";
  return [MODES[d.mode || "site"].short, d.carrier].filter(Boolean).join(" · ");
}

export const projectLabel = (o) => o.project_name || (o.project_id ? "Zakázka" : "Kancelář");

// Seznam po skupinách (PC i telefon). Pořadí = pořadí na obrazovce.
export const BUCKETS = [
  { key: "now", title: "Řešit teď", note: "nedorazilo, chybí termín nebo něco nesedí" },
  { key: "week", title: "Přijde tento týden", note: "kdo přebírá, dostane večer předem upozornění" },
  { key: "pick", title: "K vyzvednutí na pobočce", note: "někdo musí dojet" },
  { key: "later", title: "Později", note: "termín za víc než týden, nebo ho dodavatel ještě nedal" },
  { key: "got", title: "Převzato", note: "čeká na platbu — pak se samo přesune do Vyřízeno" },
];

const byDue = (a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999") || (b.created_at || "").localeCompare(a.created_at || "");

export function groupByBucket(items) {
  const g = Object.fromEntries(BUCKETS.map((b) => [b.key, []]));
  items.forEach((o) => { if (g[o.bucket]) g[o.bucket].push(o); });
  Object.values(g).forEach((list) => list.sort(byDue));
  return g;
}

// Tabule: sloupec = stav. „Na cestě“ a „K vyzvednutí“ sdílí sloupec (obojí čeká na převzetí).
export const COLUMNS = [
  { key: "ordered", title: "Objednáno", statuses: ["ordered"], bar: "bg-muted-foreground", pct: 20 },
  { key: "confirmed", title: "Potvrzeno", statuses: ["confirmed"], bar: "bg-blue-500", pct: 45 },
  { key: "way", title: "Na cestě · k vyzvednutí", statuses: ["shipped", "ready"], bar: "bg-brand", pct: 70 },
  { key: "received", title: "Převzato", statuses: ["received"], bar: "bg-success", pct: 90 },
];
export const columnOf = (o) => COLUMNS.find((c) => c.statuses.includes(o.status));
// Přetažení do sloupce „Na cestě · k vyzvednutí“: osobní odběr = k vyzvednutí, jinak na cestě.
export const statusForColumn = (col, o) => (col.key === "way" ? (o.delivery?.mode === "pickup" ? "ready" : "shipped") : col.key);

// Kalendář: řádek = stavba (bez zakázky = Kancelář), sloupec = den týdne.
export function calendarRows(items, monday) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const rows = new Map();
  items.forEach((o) => {
    if (o.status === "cancelled" || !o.due_date || o.due_date < days[0] || o.due_date > days[6]) return;
    const key = o.project_id || "office";
    if (!rows.has(key)) rows.set(key, { key, name: projectLabel(o), receiver: o.receiver_name, cells: days.map(() => []) });
    rows.get(key).cells[days.indexOf(o.due_date)].push(o);
  });
  const list = [...rows.values()].sort((a, b) => (a.key === "office") - (b.key === "office") || a.name.localeCompare(b.name, "cs"));
  return { days, rows: list };
}

// Částka: obě hodnoty z jedné, jak ji kdo zná (s DPH / bez DPH). Sazba 0 % je platná.
export function splitAmount(amount, mode, rate) {
  const x = Number(String(amount ?? "").replace(/\s/g, "").replace(",", "."));
  if (amount === "" || amount == null || !Number.isFinite(x)) return { gross: null, net: null };
  const k = 1 + (Number.isFinite(Number(rate)) ? Number(rate) : 21) / 100;
  const r2 = (v) => Math.round(v * 100) / 100;
  return mode === "net" ? { gross: r2(x * k), net: x } : { gross: x, net: r2(x / k) };
}
